import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { billing, clients } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import { PageLayout, Card, Badge, Modal, Button, Skeleton, EmptyCard } from '../../components/ui'

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'invoiced', label: 'Outstanding' },
  { key: 'paid', label: 'Paid' },
  { key: 'cancelled', label: 'Cancelled' },
]

function statusBadgeVariant(status) {
  switch (status) {
    case 'draft': return 'default'
    case 'invoiced': return 'warning'
    case 'paid': return 'primary'
    case 'cancelled': return 'error'
    default: return 'default'
  }
}

function statusLabel(status) {
  switch (status) {
    case 'draft': return 'Draft'
    case 'invoiced': return 'Outstanding'
    case 'paid': return 'Paid'
    case 'cancelled': return 'Cancelled'
    default: return status?.replace(/_/g, ' ') || 'Unknown'
  }
}

function formatCurrency(cents) {
  if (cents == null) return '$0.00'
  const dollars = typeof cents === 'number' && cents > 100 ? cents / 100 : cents
  return `$${Number(dollars).toFixed(2)}`
}

function DollarIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function RevenueSummary() {
  const { data, isLoading } = useQuery({
    queryKey: ['billingSummary'],
    queryFn: billing.summary,
  })

  if (isLoading) {
    return (
      <div className="grid grid-cols-3 gap-3">
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-20 rounded-xl" />
      </div>
    )
  }

  const summary = data || {}

  return (
    <div className="grid grid-cols-3 gap-3">
      <Card>
        <p className="text-xs text-gray-500 dark:text-indices-muted">This Month</p>
        <p className="text-xl font-bold text-primary-600 dark:text-lume-teal mt-1 font-display">
          {formatCurrency(summary.monthTotal || 0)}
        </p>
        <p className="text-xs text-gray-500 dark:text-indices-muted mt-1">Revenue</p>
      </Card>
      <Card>
        <p className="text-xs text-gray-500 dark:text-indices-muted">Outstanding</p>
        <p className="text-xl font-bold text-status-warning mt-1 font-display">
          {formatCurrency(summary.outstandingTotal || 0)}
        </p>
        <p className="text-xs text-gray-500 dark:text-indices-muted mt-1">Unpaid</p>
      </Card>
      <Card>
        <p className="text-xs text-gray-500 dark:text-indices-muted">Active</p>
        <p className="text-xl font-bold text-primary-600 dark:text-lume-teal mt-1 font-display">
          {summary.activePackages || 0}
        </p>
        <p className="text-xs text-gray-500 dark:text-indices-muted mt-1">Packages</p>
      </Card>
    </div>
  )
}

function CreatePackageModal({ isOpen, onClose }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    clientId: '',
    name: '',
    description: '',
    packageType: 'per_session',
    sessionCount: '',
    price: '',
  })
  const [error, setError] = useState(null)

  const { data: clientList } = useQuery({
    queryKey: ['clients'],
    queryFn: clients.list,
    enabled: isOpen,
  })

  const createMutation = useMutation({
    mutationFn: (data) => billing.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingPackages'] })
      queryClient.invalidateQueries({ queryKey: ['billingSummary'] })
      queryClient.invalidateQueries({ queryKey: ['outstandingCount'] })
      onClose()
      setForm({ clientId: '', name: '', description: '', packageType: 'per_session', sessionCount: '', price: '' })
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to create package')
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    setError(null)

    if (!form.clientId || !form.name || !form.price) {
      setError('Client, name, and price are required')
      return
    }

    const data = {
      clientId: Number(form.clientId),
      name: form.name,
      description: form.description || undefined,
      packageType: form.packageType,
      price: parseFloat(form.price),
    }

    if (form.packageType === 'per_session' && form.sessionCount) {
      data.sessionCount = parseInt(form.sessionCount, 10)
    }

    createMutation.mutate(data)
  }

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }))
  }

  const allClients = Array.isArray(clientList) ? clientList : (clientList?.clients || clientList || [])

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New Package" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-status-error/10 border border-status-error/30 rounded-xl text-sm text-status-error">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-indices-medium mb-1.5">
            Client <span className="text-status-error">*</span>
          </label>
          <select
            value={form.clientId}
            onChange={handleChange('clientId')}
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dial-surface border border-gray-300 dark:border-dial-border text-gray-900 dark:text-indices-bright focus:border-primary-500 dark:focus:border-lume-teal focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20 appearance-none"
          >
            <option value="">Select a client...</option>
            {allClients.map(c => (
              <option key={c.id} value={c.id} className="bg-white dark:bg-dial-surface text-gray-900 dark:text-indices-bright">
                {c.firstName} {c.lastName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-indices-medium mb-1.5">
            Package Name <span className="text-status-error">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={handleChange('name')}
            placeholder="e.g., 10-Session Training Package"
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dial-surface border border-gray-300 dark:border-dial-border text-gray-900 dark:text-indices-bright placeholder:text-gray-400 dark:placeholder:text-indices-muted focus:border-primary-500 dark:focus:border-lume-teal focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-indices-medium mb-1.5">
            Description
          </label>
          <textarea
            value={form.description}
            onChange={handleChange('description')}
            rows={2}
            placeholder="Optional description..."
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dial-surface border border-gray-300 dark:border-dial-border text-gray-900 dark:text-indices-bright placeholder:text-gray-400 dark:placeholder:text-indices-muted focus:border-primary-500 dark:focus:border-lume-teal focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20 resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-indices-medium mb-1.5">
            Package Type
          </label>
          <select
            value={form.packageType}
            onChange={handleChange('packageType')}
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dial-surface border border-gray-300 dark:border-dial-border text-gray-900 dark:text-indices-bright focus:border-primary-500 dark:focus:border-lume-teal focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20 appearance-none"
          >
            <option value="per_session">Per Session</option>
            <option value="monthly">Monthly</option>
            <option value="one_time">One Time</option>
          </select>
        </div>

        {form.packageType === 'per_session' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-indices-medium mb-1.5">
              Session Count
            </label>
            <input
              type="number"
              value={form.sessionCount}
              onChange={handleChange('sessionCount')}
              min="1"
              placeholder="e.g., 10"
              className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dial-surface border border-gray-300 dark:border-dial-border text-gray-900 dark:text-indices-bright placeholder:text-gray-400 dark:placeholder:text-indices-muted focus:border-primary-500 dark:focus:border-lume-teal focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-indices-medium mb-1.5">
            Price ($) <span className="text-status-error">*</span>
          </label>
          <input
            type="number"
            value={form.price}
            onChange={handleChange('price')}
            min="0"
            step="0.01"
            placeholder="0.00"
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dial-surface border border-gray-300 dark:border-dial-border text-gray-900 dark:text-indices-bright placeholder:text-gray-400 dark:placeholder:text-indices-muted focus:border-primary-500 dark:focus:border-lume-teal focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
            className="flex-1 py-2.5 px-4 bg-dial-elevated text-indices-bright border border-dial-border rounded-xl font-medium hover:bg-dial-surface transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="flex-1 py-2.5 px-4 bg-lume-teal text-dial-black rounded-xl font-medium hover:bg-lume-green hover:shadow-lume transition-all disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Package'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function PackageListItem({ pkg }) {
  const navigate = useNavigate()

  const sessionsUsed = pkg.sessionsUsed || 0
  const sessionCount = pkg.sessionCount || 0
  const hasProgress = pkg.packageType === 'per_session' && sessionCount > 0

  return (
    <div
      onClick={() => navigate(`/billing/${pkg.id}`)}
      className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-dial-elevated transition-colors"
    >
      <div className="w-10 h-10 bg-lume-teal/20 text-lume-teal rounded-full flex items-center justify-center flex-shrink-0">
        <DollarIcon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-900 dark:text-indices-bright truncate">
            {pkg.name}
          </p>
          <Badge variant={statusBadgeVariant(pkg.status)} size="sm">
            {statusLabel(pkg.status)}
          </Badge>
        </div>
        <p className="text-xs text-gray-500 dark:text-indices-muted truncate mt-0.5">
          {pkg.clientName || 'No client'}
          {hasProgress && ` \u00b7 ${sessionsUsed}/${sessionCount} sessions`}
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-semibold text-primary-600 dark:text-lume-teal font-display">
          {formatCurrency(pkg.price)}
        </p>
        {hasProgress && (
          <div className="w-16 h-1.5 bg-gray-200 dark:bg-dial-elevated rounded-full mt-1.5 overflow-hidden">
            <div
              className="h-full bg-lume-teal rounded-full transition-all"
              style={{ width: `${Math.min(100, (sessionsUsed / sessionCount) * 100)}%` }}
            />
          </div>
        )}
      </div>
      <svg className="w-5 h-5 text-gray-400 dark:text-indices-muted flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  )
}

export default function BillingPage() {
  const [searchParams] = useSearchParams()
  const clientIdFilter = searchParams.get('clientId')
  const [activeTab, setActiveTab] = useState('all')
  const [showCreate, setShowCreate] = useState(false)

  const queryParams = {}
  if (activeTab !== 'all') queryParams.status = activeTab
  if (clientIdFilter) queryParams.clientId = clientIdFilter

  const { data: packagesData, isLoading } = useQuery({
    queryKey: ['billingPackages', queryParams],
    queryFn: () => billing.list(queryParams),
  })

  const packages = packagesData?.packages || packagesData || []
  const packageList = Array.isArray(packages) ? packages : []

  return (
    <PageLayout>
      <PageHeader
        title="Billing"
        showBack
        backTo="/"
        actions={
          <button
            onClick={() => setShowCreate(true)}
            className="px-3 py-1.5 bg-lume-teal text-dial-black rounded-lg text-sm font-medium hover:bg-lume-green hover:shadow-lume transition-all"
          >
            + New Package
          </button>
        }
      />

      <div className="px-4 py-4 space-y-4">
        {/* Subscription Plans Link */}
        <Link
          to="/billing/subscriptions"
          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dial-surface border border-gray-200 dark:border-dial-border rounded-xl hover:bg-gray-100 dark:hover:bg-dial-elevated transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-lume-teal/20 text-lume-teal rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-indices-bright">Subscription Plans</span>
          </div>
          <svg className="w-5 h-5 text-gray-400 dark:text-indices-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>

        {/* Revenue Summary */}
        <RevenueSummary />

        {/* Filter Tabs */}
        <div className="flex gap-1 bg-gray-100 dark:bg-dial-surface border border-gray-200 dark:border-dial-border rounded-xl p-1">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-white dark:bg-lume-teal text-gray-900 dark:text-dial-black shadow-sm'
                  : 'text-gray-600 dark:text-indices-medium hover:text-gray-900 dark:hover:text-indices-bright'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Package List */}
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-20 rounded-xl" />
            <Skeleton className="h-20 rounded-xl" />
            <Skeleton className="h-20 rounded-xl" />
          </div>
        ) : packageList.length === 0 ? (
          <EmptyCard
            icon={<DollarIcon className="w-6 h-6" />}
            title="No packages found"
            description={activeTab !== 'all' ? `No ${activeTab} packages` : 'Create your first billing package to get started'}
            action={
              activeTab === 'all' && (
                <button
                  onClick={() => setShowCreate(true)}
                  className="px-4 py-2 bg-lume-teal text-dial-black rounded-xl font-medium hover:bg-lume-green hover:shadow-lume transition-all"
                >
                  Create Package
                </button>
              )
            }
          />
        ) : (
          <Card padding="none">
            <div className="divide-y divide-gray-200 dark:divide-dial-border">
              {packageList.map(pkg => (
                <PackageListItem key={pkg.id} pkg={pkg} />
              ))}
            </div>
          </Card>
        )}
      </div>

      <CreatePackageModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
      />
    </PageLayout>
  )
}
